// SH-2102: dropdown for the expandable "Activities" chip. Standard MUI v5
// primitives only (Menu/MenuItem/Checkbox/ListSubheader) -- the app is on
// MUI v5, Resonance needs v7+, so this deliberately avoids @workwave-tidal
// coupling to keep a future migration smaller (per the groomed spec).

import * as React from 'react'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import ListSubheader from '@mui/material/ListSubheader'
import Checkbox from '@mui/material/Checkbox'
import ListItemText from '@mui/material/ListItemText'
import Divider from '@mui/material/Divider'
import { useTheme } from '@mui/material/styles'
import type { ActivityType } from '../../hooks/useActivityTypeFilter'

type Props = {
  activityTypes: ActivityType[]
  selectedIds: Set<string>
  onToggleType: (id: string) => void
  onSelectAll: () => void
  onClearAll: () => void
  anchorEl: HTMLElement | null
  open: boolean
  onClose: () => void
}

export function ActivityTypeFilterMenu({
  activityTypes,
  selectedIds,
  onToggleType,
  onSelectAll,
  onClearAll,
  anchorEl,
  open,
  onClose,
}: Props) {
  const theme = useTheme()

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      slotProps={{
        paper: {
          sx: { minWidth: 288, maxHeight: 380, overflowY: 'auto' },
        },
      }}
      MenuListProps={{ dense: true, disablePadding: true }}
    >
      <ListSubheader
        sx={{
          fontFamily: theme.typography.fontFamily,
          fontSize: '11px',
          fontWeight: 700,
          letterSpacing: '0.04em',
          lineHeight: '32px',
        }}
      >
        ACTIVITY TYPES
      </ListSubheader>
      {/* Direct Menu/MenuList children, not wrapped in a Box: MUI's MenuList
          keyboard handler (arrow keys, Home/End, initial autofocus) walks
          list.firstChild / item.nextElementSibling at the top level, so a
          wrapping element would make these two unreachable by keyboard. */}
      <MenuItem
        onClick={onSelectAll}
        dense
        disableGutters
        sx={{
          width: 'auto',
          display: 'inline-flex',
          pl: 2,
          color: theme.palette.primary.main,
        }}
      >
        Select all
      </MenuItem>
      <MenuItem
        onClick={onClearAll}
        dense
        disableGutters
        sx={{
          width: 'auto',
          display: 'inline-flex',
          pl: 2,
          mb: 1,
          color: theme.palette.primary.main,
        }}
      >
        Clear all
      </MenuItem>
      <Divider />
      {activityTypes.map((type) => {
        const checked = selectedIds.has(type.id)
        const label = type.name ?? type.id
        return (
          <MenuItem
            key={type.id}
            onClick={() => onToggleType(type.id)}
            dense
            role="menuitemcheckbox"
            aria-checked={checked}
          >
            <Checkbox
              checked={checked}
              size="small"
              tabIndex={-1}
              inputProps={{ 'aria-label': label }}
              onClick={(e) => e.stopPropagation()}
              onChange={() => onToggleType(type.id)}
            />
            <ListItemText primary={label} />
          </MenuItem>
        )
      })}
    </Menu>
  )
}
